<template>
    <div class="main-custom">
        <div class="header-div">
            <div class="title-div">
                <div class="icon-text-header">
                    <div class="icon-div">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="380"
                            height="380"
                            viewBox="0 0 480 480"
                            fill="none"
                            class="pattern"
                            opacity="0.4"
                        >
                            <defs>
                                <clipPath id="clip-circle"><rect x="190" y="190" width="240" height="240" /></clipPath>
                            </defs>
                            <g clip-path="url(#clip-circle)">
                                <circle cx="240" cy="240" r="60" stroke="currentColor" />
                                <circle cx="240" cy="240" r="92" stroke="currentColor" />
                                <circle cx="240" cy="240" r="124" stroke="currentColor" />
                                <circle cx="240" cy="240" r="156" stroke="currentColor" />
                            </g>
                        </svg>
                        <PhPaperPlaneTilt :size="20" class="text-zinc-900" />
                    </div>
                    <div>
                        <div class="title-section">
                            <p>Custom Removal Request</p>
                        </div>
                        <p class="sub-title">
                            Found your personal information exposed on a website? Enter the URL below and we'll attempt to remove
                            it.
                        </p>
                        <ModalField
                            class="w-full"
                            label="Website URL"
                            input-id="removal-url"
                            :error="isUrlInvalid ? 'Please enter a valid URL (e.g., https://example.com).' : undefined"
                            v-slot="{ errorId, invalid }"
                        >
                            <!-- The box is the field: the text field inside it is borderless, so
                                 the ring it gives up (`data-focus-ring`) is drawn round the box by
                                 the design system's `pb-field-shell`, and the prefix sits inside it. -->
                            <div class="url-input-wrapper pb-field-shell" :class="{ 'invalid': invalid }">
                                <span id="removal-url-prefix" class="url-prefix">https://</span>
                                <PrimeInputText
                                    id="removal-url"
                                    autofocus
                                    data-focus-ring="none"
                                    v-model="url"
                                    type="url"
                                    placeholder="example.com/profile/..."
                                    :aria-describedby="`removal-url-prefix ${errorId}`"
                                    @keyup.enter="handleSubmit"
                                    :invalid="invalid"
                                    class="url-field outline-none"
                                />
                            </div>
                        </ModalField>
                    </div>
                </div>
            </div>
        </div>
        <div class="footer">
            <div class="footer-actions">
                <prime-button @click="closeDialog" class="p-btn-root_secondary" label="Cancel" :disabled="isSubmitting" />

                <prime-button
                    @click="handleSubmit"
                    label="Submit Request"
                    :disabled="isSubmitting || url === ''"
                    :loading="isSubmitting"
                />
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import ModalField from '~/components/modals/shared/ModalField.vue';
import { CustomRemovalError, exposuresService } from '~/services/exposures-service';
import { usePersonStore } from '~/stores/person';
import { ModalNames } from '~/types/modals';

const { showSuccess, showError } = useGlobalToast();
const { hasActiveLicense, gate, promptForGate } = useLicenseAccess();
const personStore = usePersonStore();

const url = ref('');
const isSubmitting = ref(false);

// Strip protocol prefix if user pastes a full URL (e.g. https://company.com → company.com)
// Also clear any submit-triggered error so it doesn't linger while the user keeps typing
watch(url, (newVal) => {
    const stripped = newVal.replace(/^https?:\/\//i, '');
    if (stripped !== newVal) {
        url.value = stripped;
    }
    isUrlInvalid.value = false;
});

// Full URL with the fixed https:// prefix
const fullUrl = computed(() => url.value.trim() ? `https://${url.value.trim()}` : '');

// Validate domain.tld format (https:// is always prepended)
const isValidUrl = (value: string): boolean => {
    if (!value.trim()) return false;
    const domainPattern = /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(\/.*)?$/;
    return domainPattern.test(value.trim());
};

const isUrlInvalid = ref(false);

const dialogRef = getDialogRef(ModalNames.REMOVAL_REQUEST);
const closeDialog = () => {
    dialogRef.value.close();
};

const handleSubmit = async () => {
    if (isSubmitting.value) return;

    isUrlInvalid.value = !isValidUrl(url.value);

    if (!personStore.person?.id) {
        showError('Error', 'Unable to identify user. Please try again.');
        return;
    }

    if (!fullUrl.value || isUrlInvalid.value) {
        return;
    }

    isSubmitting.value = true;

    try {
        const response = await exposuresService.submitCustomRemoval(personStore.person.id, fullUrl.value);
        showSuccess('Success', response.message || 'Removal request submitted successfully.');
        closeDialog();
    } catch (error: any) {
        if (error instanceof CustomRemovalError) {
            if (error.alreadyExists) {
                // Exposure already exists - show info toast and close
                showError('Already Submitted', error.message);
                closeDialog();
            } else if (error.requiresUpgrade) {
                // Close this dialog and open the purchase/upgrade license dialog
                closeDialog();

                // The backend refused for want of coverage: a protected member needs a bigger
                // plan, an unprotected one needs a seat.
                promptForGate(hasActiveLicense.value ? 'needs_upgrade' : gate.value);
            } else {
                // Non-closing error - re-enable the button so user can retry
                isSubmitting.value = false;
                showError('Error', error.message || 'Failed to submit removal request. Please try again.');
            }
        } else {
            // Non-closing error - re-enable the button so user can retry
            isSubmitting.value = false;
            showError('Error', error.message || 'Failed to submit removal request. Please try again.');
        }
    }
};
</script>
<style lang="scss" scoped>
.main-custom {
    @apply w-11/12 lg:w-[500px] rounded-lg bg-white mx-auto h-auto overflow-y-auto;
}
.header-div {
    @apply flex flex-row items-start gap-4 self-stretch;
}
.title-div {
    @apply flex flex-col p-6 pb-0 items-start gap-1 self-stretch w-full;
}
.icon-text-header {
    @apply flex flex-row items-start gap-4 items-start w-full;
}
.icon-div {
    @apply hidden lg:flex relative flex w-12 h-12 p-3 justify-center items-center rounded-full bg-gray-100;
}
.title-section {
    @apply self-stretch flex flex-row justify-between relative;

    p {
        @apply text-zinc-950 text-lg font-semibold leading-7;
    }
}
.close-icon {
    @apply flex justify-center items-center rounded-lg text-gray-400 cursor-pointer;
}
.sub-title {
    @apply text-zinc-600 text-sm font-normal leading-5 pb-6;
}
.note {
    @apply flex my-4 items-start rounded-xl border border-zinc-300 bg-white p-4 shadow-cardshadow;
}
.note-details {
    @apply flex flex-1 items-start gap-4 pr-0 lg:pr-8;
}
.note-text {
    @apply flex flex-col items-start gap-1;

    span {
        @apply text-zinc-900 text-sm font-semibold leading-5;
    }

    p {
        @apply text-zinc-600 text-sm font-normal leading-5;
    }
}
.pattern {
    @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none;
    color: var(--hairline);
}
.divider {
    @apply h-px bg-zinc-300 mb-4 w-full;
}
.footer {
    @apply flex flex-col justify-center items-end self-stretch pt-8;

    .footer-actions {
        @apply flex justify-end items-center gap-3 flex-1 px-6 pb-6;

        .p-btn-root_primary {
            @apply sm:px-3 px-1 py-2;
        }
    }
}
.url-input-wrapper {
    @apply flex items-center w-full rounded border border-zinc-300 bg-white overflow-hidden;

    &.invalid {
        @apply border-red-500;
    }

    .url-prefix {
        @apply pl-3 pr-3 text-zinc-600 text-base font-normal leading-6 select-none whitespace-nowrap border-r border-zinc-300 self-stretch flex items-center;
    }

    .url-field {
        @apply flex-1 border-none shadow-none pl-2;
    }
}
</style>
